import { useEffect, useRef, useState } from "react";
import Proptypes from "prop-types";
import { caseTransformingAxios } from "@shared/v2/caseTransformingAxios";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Tooltip from "@shared/v2/Tooltip/Tooltip";
import MarketerOnboardingTooltip from "@shared/v2/MarketerOnboardingTooltip";
import { isUSCountry } from "@shared/v2/utils";
import SelectMailerTypeModal from "../../PersonDetail/components/modals/SelectMailerTypeModal";
import { mapToContactPayload } from "../../PersonDetail/components/listing_alerts/helper";
import { useCurrentUser } from "../../reducers/layoutReducer/selectors";
import { getMarketerOnboardingStatus } from "../../PersonDetail/actions/personDetailActionCreators";
import CreateMailerCampaignModal from "../CreateMailerCampaignModal";

const MailerOption = ({ isOnboarded = false, onClick = () => {} }) => (
  <button
    type="button"
    id="mass-mailer-campaign"
    className={`tw-clear-both tw-flex tw-w-[251px] tw-items-center tw-justify-between tw-border-0 tw-bg-transparent tw-px-[10px] tw-py-[8px] tw-text-left tw-font-normal tw-normal-case ${!isOnboarded ? "!tw-cursor-default !tw-bg-[#f0f0f0] !tw-text-[#cccccc]" : "tw-text-gray-dark hover:tw-bg-gray-5 hover:tw-text-gray-dark"}`}
    onClick={() => isOnboarded && onClick()}
  >
    <span>Mailer Campaign</span>
    <span className="tw-ml-auto tw-inline-flex tw-h-[20px] tw-items-center tw-rounded-[4px] tw-bg-[#59c4c4] tw-px-[4px] tw-py-[2px] tw-text-[9.319px] tw-font-extrabold tw-uppercase tw-leading-none tw-text-white">
      New
    </span>
  </button>
);

MailerOption.propTypes = {
  isOnboarded: Proptypes.bool,
  onClick: Proptypes.func,
};

const MIN_SMART_FILTER_RECIPIENTS = 50;

const getTotalRecipientCount = () => {
  const countElement = document.querySelector("#header-count");
  const countLabel = countElement?.innerText || countElement?.textContent || "";
  const recipientCount = parseInt(countLabel.replace(/[(),]/g, ""), 10);

  return Number.isNaN(recipientCount) ? 0 : recipientCount;
};

const allPeopleSmartFilter = () => ({
  id: "allPeople",
  name: "All People",
  recipientCount: getTotalRecipientCount(),
});

const MassSendMailerCampaign = () => {
  const [showModal, setShowModal] = useState(false);
  const [showCreateMailerCampaignModal, setShowCreateMailerCampaignModal] = useState(false);
  const [isMatchingSmartFilter, setIsMatchingSmartFilter] = useState(false);
  const [smartFilterMatches, setSmartFilterMatches] = useState([]);
  const [smartFilterMatchCount, setSmartFilterMatchCount] = useState(0);
  const [selectedSmartFilter, setSelectedSmartFilter] = useState(null);
  const [contacts, setContacts] = useState([]);
  const isCreatingSmartFilter = useRef(false);

  const isAdmin = useCurrentUser()?.account?.isAdmin;
  const isAgent = useCurrentUser()?.account?.isAgent;
  const dispatch = useDispatch();
  const marketerOnboardingStatus = useSelector((state) => state.personDetail.marketerOnboardingStatus);
  const marketerIndexIntegrationFeature = useSelector(
    (state) => state.leadIndex.marketerIndexIntegrationFeature,
  );
  useEffect(() => {
    if (marketerIndexIntegrationFeature) {
      dispatch(getMarketerOnboardingStatus());
    }
  }, []);

  const { team_onboarded: teamOnboarded, agent_onboarded: agentOnboarded } =
    marketerOnboardingStatus?.data || {};

  const isOnboarded = (isAdmin && teamOnboarded) || (isAgent && teamOnboarded && agentOnboarded) || false;

  const currentUser = useCurrentUser();
  const isUSAddress = isUSCountry(currentUser?.account?.country);

  const getValidLeads = () => {
    const path = `/marketer/people`;
    const ids = Brivity.PeoplePage.grid().multiSelect().getSelectedPersonUuids();
    axios
      .post(path, {
        people_uuids: ids,
        authenticity_token: window.ReactOnRails.authenticityToken(),
      })
      .then((res) => {
        setContacts(mapToContactPayload(res.data?.people));
      });
  };

  useEffect(() => {
    if (showModal && !selectedSmartFilter) {
      getValidLeads();
    }
  }, [showModal, selectedSmartFilter]);

  const createSmartFilter = async (filterName) => {
    if (isCreatingSmartFilter.current) return;

    isCreatingSmartFilter.current = true;
    try {
      const { data } = await caseTransformingAxios.post("/user_filters.json", { name: filterName });
      if (!data?.id) throw new Error("Smart Filter creation did not return an ID");

      setSelectedSmartFilter({
        id: data.id,
        name: data.name || filterName,
        recipientCount: getTotalRecipientCount(),
      });
      setShowCreateMailerCampaignModal(false);
      setShowModal(true);
    } finally {
      isCreatingSmartFilter.current = false;
    }
  };

  const selectExistingSmartFilter = (filter) => {
    setSelectedSmartFilter({
      id: Number(filter.id),
      name: filter.name,
      recipientCount: getTotalRecipientCount(),
    });
    setShowCreateMailerCampaignModal(false);
    setShowModal(true);
  };

  const handleCreateMailerCampaignNext = (value) =>
    typeof value === "string" ? createSmartFilter(value) : selectExistingSmartFilter(value);

  const handleMailerCampaignClick = async () => {
    if (isMatchingSmartFilter) return;

    const selectedLeadCount = Brivity.PeoplePage.grid().multiSelect().getSelectedPersonUuids().length;
    const hasFilters = Boolean(document.querySelector("#applied-filters"));
    const isAllSelected = document.getElementById("is-all-selected")?.value === "true";
    const hasMassSelection = isAllSelected && selectedLeadCount >= MIN_SMART_FILTER_RECIPIENTS;
    const requiresSmartFilterMatch = hasMassSelection && hasFilters;

    if (!requiresSmartFilterMatch) {
      setSelectedSmartFilter(hasMassSelection ? allPeopleSmartFilter() : null);
      setShowModal(true);
      return;
    }

    setShowModal(false);
    setShowCreateMailerCampaignModal(false);
    setSelectedSmartFilter(null);
    setSmartFilterMatches([]);
    setSmartFilterMatchCount(0);
    setIsMatchingSmartFilter(true);

    try {
      const { data } = await axios.get("/accounts/match_smart_filter");
      const matchCount = data?.match_count;
      const matches = data?.matches || [];

      if (matchCount === 1 && matches[0]) {
        selectExistingSmartFilter(matches[0]);
      } else if (matchCount === 0 || matchCount >= 2) {
        setSmartFilterMatchCount(matchCount);
        setSmartFilterMatches(matches);
        setShowCreateMailerCampaignModal(true);
      }
    } catch {
      setShowModal(false);
      setShowCreateMailerCampaignModal(false);
    } finally {
      setIsMatchingSmartFilter(false);
    }
  };

  return (
    isUSAddress && (
      <>
        <CreateMailerCampaignModal
          show={showCreateMailerCampaignModal}
          onHide={() => setShowCreateMailerCampaignModal(false)}
          onNext={handleCreateMailerCampaignNext}
          matchCount={smartFilterMatchCount}
          matches={smartFilterMatches}
        />
        <li className="divider" />
        <li>
          {!isOnboarded ? (
            <Tooltip
              innerClassName="tw-max-w-[250px]"
              placement="left"
              multiline
              content={<MarketerOnboardingTooltip />}
              trigger={<MailerOption isOnboarded={isOnboarded} onClick={handleMailerCampaignClick} />}
            />
          ) : (
            <MailerOption isOnboarded={isOnboarded} onClick={handleMailerCampaignClick} />
          )}

          {isOnboarded && (
            <SelectMailerTypeModal
              setOpen={() => setShowModal(false)}
              isModalOpen={showModal}
              personData={contacts}
              smartFilter={selectedSmartFilter}
            />
          )}
        </li>
      </>
    )
  );
};

export default MassSendMailerCampaign;
